import { useEffect, useMemo, useState } from "react";
import { Shell, Card } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FeesTable } from "../components/Billing";
import { PauseCard } from "../components/PauseCard";
import { WeekGrid } from "../components/ScheduleGrid";
import { apiError, http, DAYS, GROUPS, BLOCK_SUBJECTS, MONTH_LABELS, groupLabel,
  monthName, thisMonthISO, slotMatchesGroup, todayISO, monthEnd } from "../lib/api";

function ExamCalendar({ exams }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const prev = () => (month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1));
  const next = () => (month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1));

  return (
    <div data-testid="exam-calendar">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Button data-testid="cal-prev-btn" variant="outline" className="h-12 text-base" onClick={prev}>Mes anterior</Button>
        <p className="font-head text-xl font-medium">{MONTH_LABELS[month]} {year}</p>
        <Button data-testid="cal-next-btn" variant="outline" className="h-12 text-base" onClick={next}>Mes siguiente</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium text-ink-700">
        {["L", "M", "X", "J", "V", "S", "D"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const iso = d ? `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
          const dayExams = iso ? exams.filter((e) => e.date === iso) : [];
          return (
            <div key={i} className={`min-h-[74px] rounded-lg border p-1 text-left ${
              d ? "border-border bg-white" : "border-transparent"}`}>
              {d && <span className="text-sm text-ink-700">{d}</span>}
              {dayExams.map((e) => (
                <p key={e.id} data-testid={`cal-exam-${e.id}`}
                  className="mt-1 rounded bg-centro-light px-1 text-xs font-medium text-centro-text">
                  {e.subject}
                </p>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PerfilCentro() {
  const [students, setStudents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [exams, setExams] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [examForm, setExamForm] = useState({ subject: "", date: "", note: "" });
  const [editing, setEditing] = useState(null);
  const [absForm, setAbsForm] = useState({ slot_id: "", date: "" });
  const [recForm, setRecForm] = useState({});
  const [confirmBaja, setConfirmBaja] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    const [st, sl, ex, ab] = await Promise.all([
      http.get("/students"), http.get("/slots"), http.get("/exams"), http.get("/absences"),
    ]);
    setStudents(st.data);
    setSlots(sl.data);
    setExams(ex.data);
    setAbsences(ab.data);
    setActive((cur) => cur || st.data[0]?.id || null);
  };

  useEffect(() => { load(); }, []);

  const student = useMemo(() => students.find((s) => s.id === active), [students, active]);
  const myExams = exams.filter((e) => e.student_id === active);
  const myAbs = absences.filter((a) => a.student_id === active);
  const enrolledIds = student ? student.slots.map((s) => s.id) : [];
  const groupSlots = student ? slots.filter((s) => slotMatchesGroup(s, student.stage_group)) : [];

  const act = async (fn, okMsg) => {
    setMsg(""); setError("");
    try { await fn(); setMsg(okMsg); await load(); }
    catch (err) { setError(apiError(err)); }
  };

  if (students.length === 0)
    return (
      <Shell accent="centro" title="Centro de Estudios" testid="perfil-centro-page">
        <Card className="max-w-2xl">
          <p className="text-lg">Actualmente no consta ningún alumno matriculado.</p>
          <Button data-testid="perfil-add-student-btn" className="mt-6 h-14 bg-centro text-lg hover:bg-centro-hover"
            onClick={() => (window.location.href = "/centro/matricula")}>
            Matricular a un alumno
          </Button>
        </Card>
      </Shell>
    );

  return (
    <Shell accent="centro" title="Mi área del Centro de Estudios"
      subtitle="Seleccione el alumno cuya información desea consultar." testid="perfil-centro-page">
      <div className="mb-6 flex flex-wrap gap-3">
        {students.map((s) => (
          <Button key={s.id} data-testid={`child-tab-${s.id}`} onClick={() => setActive(s.id)}
            className={`h-14 text-lg ${active === s.id ? "bg-centro hover:bg-centro-hover" : "bg-white text-ink-900 hover:bg-centro-light"}`}>
            {s.name}
          </Button>
        ))}
        <Button data-testid="perfil-add-student-btn" variant="outline" className="h-14 text-lg"
          onClick={() => (window.location.href = "/centro/matricula")}>
          Matricular a otro alumno
        </Button>
      </div>

      {msg && <p data-testid="perfil-success" className="mb-5 rounded-xl bg-logo-light p-4 text-lg text-logo-text">{msg}</p>}
      {error && <p data-testid="perfil-error" className="mb-5 rounded-xl bg-red-50 p-4 text-lg text-red-700">{error}</p>}

      {student && (
        <>
          {/* 1. HORARIO */}
          <Card className="mb-8" data-testid="section-horario">
            <h2 className="font-head text-2xl font-medium">Horario de {student.name}</h2>
            <p className="mt-1 text-base text-ink-700">{groupLabel(student.stage_group)}</p>
            {student.pause_month && (
              <p data-testid="pause-notice" className="mt-3 rounded-xl bg-cream p-4 text-lg">
                Matrícula pausada en {monthName(student.pause_month)}. Se reactivará automáticamente
                con este mismo horario.
              </p>
            )}
            <ul className="mt-4 space-y-4">
              {student.slots.map((s) => (
                <li key={s.id} className="rounded-xl border border-border p-4" data-testid={`my-slot-${s.id}`}>
                  <p className="text-lg font-medium">{DAYS[s.day]} de {s.start} a {s.end}</p>
                  <p className="text-base text-ink-700">
                    {s.label}{s.block ? ` · ${s.block}` : ""}{s.kind === "gr" ? " · Grupo Reducido" : ""}
                    {s.teacher ? ` · ${s.teacher}` : ""}
                  </p>
                  {s.block && BLOCK_SUBJECTS[s.block] && (
                    <p className="mt-1 text-base text-ink-700" data-testid={`my-slot-subjects-${s.id}`}>
                      Asignaturas de {s.block}: {BLOCK_SUBJECTS[s.block].join(", ")}.
                    </p>
                  )}
                  <div className="mt-3">
                    <Label className="text-base">Cambiar esta clase a otra franja de la misma agrupación</Label>
                    <select data-testid={`change-select-${s.id}`}
                      className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                      defaultValue=""
                      onChange={(e) => {
                        const to = e.target.value;
                        if (!to) return;
                        act(() => http.post("/enrollments/change", {
                          student_id: student.id, from_slot_id: s.id, to_slot_id: to,
                        }), "Cambio de franja realizado correctamente.");
                      }}>
                      <option value="">Seleccionar nueva franja...</option>
                      {groupSlots.filter((g) => !enrolledIds.includes(g.id) && !g.full &&
                        (g.kind === "gr") === (s.kind === "gr")).map((g) => (
                        <option key={g.id} value={g.id}>
                          {`${DAYS[g.day]} ${g.start}-${g.end} · ${g.label}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8" data-testid="full-week-schedule">
              <h3 className="font-head text-xl font-medium">Horario completo del centro</h3>
              <p className="mt-1 text-base text-ink-700">
                De lunes a viernes, de 15:30 a 21:30. Las franjas de {student.name} aparecen
                resaltadas en color; el resto se muestran atenuadas, solo a título informativo.
              </p>
              <div className="mt-4">
                <WeekGrid slots={slots} enrolledIds={enrolledIds}
                  isEligible={(s) => slotMatchesGroup(s, student.stage_group)} />
              </div>
            </div>
            <div className="mt-6 rounded-xl bg-cream p-5 text-lg" data-testid="fee-box">
              <p>Horas semanales en grupo grande: <strong>{student.weekly_hours}</strong> (máximo 4)</p>
              <p>Horas semanales de Grupo Reducido: <strong>{student.gr_weekly_hours}</strong> (máximo 2)</p>
              <p className="mt-3 font-medium">Importe a pagar de {monthName(student.month)}</p>
              {student.month !== thisMonthISO() && (
                <p className="text-base text-ink-700" data-testid="billing-month-note">
                  Corresponde al primer mes de clases; este mes todavía no genera cuota.
                </p>
              )}
              <p className="mt-1">Grupo grande ({student.tier}): <strong>{student.big_fee} €</strong></p>
              {student.gr_weekly_hours > 0 && (
                <p>Grupo Reducido: {student.gr_sessions_month} sesiones este mes ={" "}
                  <strong>{student.gr_fee} €</strong></p>
              )}
              {student.september_discount && (
                <p className="mt-1" data-testid="september-discount">
                  Importe sin descuento: {student.base_fee.toFixed(2)} € ·{" "}
                  <strong>descuento del {student.discount_percent} % aplicado solo a septiembre</strong>,
                  porque el curso comienza el 14 de septiembre.
                </p>
              )}
              <p className="mt-2 text-xl">A pagar este mes:{" "}
                <strong data-testid="monthly-fee">{student.monthly_fee.toFixed(2)} €</strong></p>
              <p className="mt-2 text-base text-ink-700">
                El importe se calcula cada mes según las horas del grupo grande y las sesiones
                realmente realizadas en Grupo Reducido, por lo que puede variar de un mes a otro. Se
                abona dentro de los <strong>primeros 15 días del mes</strong>, en el centro.
              </p>
            </div>
          </Card>

          {/* 1b. CUOTAS Y PAGOS DEL CURSO */}
          <Card className="mb-8" data-testid="section-cuotas">
            <h2 className="font-head text-2xl font-medium">Cuotas y pagos del curso</h2>
            <p className="mt-2 text-lg text-ink-700">
              Curso académico completo, de septiembre a junio. El importe de cada mes se calcula
              según las horas del grupo grande y las sesiones realizadas en Grupo Reducido, con el
              descuento del 30 % aplicado únicamente a septiembre. El equipo de SISU registra las
              cantidades abonadas; lo pendiente y la deuda total se calculan automáticamente.
            </p>
            <div className="mt-5">
              <FeesTable rows={student.fees} totalDebt={student.total_debt} testid="fees-table"
                note="Los meses anteriores al inicio de la matrícula y los meses en pausa no generan cuota." />
            </div>
          </Card>

          {/* 2. COMUNICAR AUSENCIA */}
          <Card className="mb-8" data-testid="section-ausencias">
            <h2 className="font-head text-2xl font-medium">Comunicar una ausencia</h2>
            <p className="mt-2 text-base text-ink-700">
              Si no se comunica la ausencia, la clase se contabilizará como asistida.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-lg">Clase</Label>
                <select data-testid="absence-slot-select" value={absForm.slot_id}
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  onChange={(e) => setAbsForm({ ...absForm, slot_id: e.target.value })}>
                  <option value="">Seleccionar clase...</option>
                  {student.slots.map((s) => (
                    <option key={s.id} value={s.id}>{`${DAYS[s.day]} ${s.start}-${s.end}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="absdate" className="text-lg">Día de la ausencia</Label>
                <Input id="absdate" data-testid="absence-date-input" type="date" className="mt-2 h-14 text-lg"
                  min={todayISO()} value={absForm.date}
                  onChange={(e) => setAbsForm({ ...absForm, date: e.target.value })} />
              </div>
            </div>
            <p className="mt-3 text-base text-ink-700">
              La ausencia debe comunicarse <strong>antes</strong> de la fecha y la hora de la clase.
              Una vez pasada la hora, esa sesión ya no puede recuperarse.
            </p>
            <Button data-testid="absence-submit-btn" disabled={!absForm.slot_id || !absForm.date}
              className="mt-4 h-14 bg-centro text-lg hover:bg-centro-hover"
              onClick={() => act(() => http.post("/absences", { student_id: student.id, ...absForm }),
                "Ausencia registrada. Podrá recuperar esa clase antes de fin de mes.")}>
              Registrar la ausencia
            </Button>

            <h3 className="mt-8 font-head text-xl font-medium">Clases pendientes de recuperar</h3>
            <ul className="mt-3 space-y-4" data-testid="absences-list">
              {myAbs.filter((a) => !a.recovered && !a.expired).length === 0 && (
                <li className="text-lg text-ink-700">No hay clases pendientes de recuperar.</li>
              )}
              {myAbs.filter((a) => !a.recovered && !a.expired).map((a) => (
                <li key={a.id} className="rounded-xl border border-border p-4" data-testid={`absence-${a.id}`}>
                  <p className="text-lg">Clase del {a.date} ({a.slot})</p>
                  <p className="text-base text-ink-700">
                    Plazo de recuperación: hasta el {a.deadline} (último día del mes de la ausencia).
                    Las recuperaciones no pasan al mes siguiente.
                  </p>
                  <div className="mt-3 space-y-3">
                    <select data-testid={`recover-slot-${a.id}`}
                      className="h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                      value={recForm[a.id]?.slot_id || ""}
                      onChange={(e) => setRecForm({ ...recForm, [a.id]: { ...recForm[a.id], slot_id: e.target.value } })}>
                      <option value="">Seleccionar franja de recuperación...</option>
                      {groupSlots.filter((g) => !g.full && !g.pending_min &&
                        (g.kind === "gr") === (a.kind === "gr")).map((g) => (
                        <option key={g.id} value={g.id}>{`${DAYS[g.day]} ${g.start}-${g.end} · ${g.label}`}</option>
                      ))}
                    </select>
                    <Input data-testid={`recover-date-${a.id}`} type="date" className="h-14 text-lg"
                      min={todayISO() > a.date ? todayISO() : a.date}
                      max={monthEnd(a.date)}
                      value={recForm[a.id]?.date || ""}
                      onChange={(e) => setRecForm({ ...recForm, [a.id]: { ...recForm[a.id], date: e.target.value } })} />
                    <Button data-testid={`recover-submit-${a.id}`} className="h-14 w-full bg-centro text-lg hover:bg-centro-hover"
                      disabled={!recForm[a.id]?.slot_id || !recForm[a.id]?.date}
                      onClick={() => act(() => http.post("/absences/recover", {
                        absence_id: a.id, ...recForm[a.id],
                      }), "Recuperación confirmada.")}>
                      Recuperar esta clase
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            {myAbs.filter((a) => a.expired).length > 0 && (
              <>
                <h3 className="mt-8 font-head text-xl font-medium">Recuperaciones caducadas</h3>
                <ul className="mt-3 space-y-2 text-lg text-ink-700" data-testid="expired-absences-list">
                  {myAbs.filter((a) => a.expired).map((a) => (
                    <li key={a.id} data-testid={`absence-expired-${a.id}`}>
                      Ausencia del {a.date} · el plazo terminó el {a.deadline} y ya no puede recuperarse
                    </li>
                  ))}
                </ul>
              </>
            )}
            {myAbs.filter((a) => a.recovered).length > 0 && (
              <>
                <h3 className="mt-8 font-head text-xl font-medium">Clases recuperadas</h3>
                <ul className="mt-3 space-y-2 text-lg text-ink-700">
                  {myAbs.filter((a) => a.recovered).map((a) => (
                    <li key={a.id}>Ausencia del {a.date} · recuperada el {a.recovery?.label}</li>
                  ))}
                </ul>
              </>
            )}
          </Card>

          {/* 3. EXÁMENES */}
          <Card className="mb-8" data-testid="section-examenes">
            <h2 className="font-head text-2xl font-medium">Exámenes de {student.name}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="subj" className="text-lg">Asignatura</Label>
                <Input id="subj" data-testid="exam-subject-input" className="mt-2 h-14 text-lg"
                  value={examForm.subject} onChange={(e) => setExamForm({ ...examForm, subject: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edate" className="text-lg">Fecha</Label>
                <Input id="edate" data-testid="exam-date-input" type="date" className="mt-2 h-14 text-lg"
                  value={examForm.date} onChange={(e) => setExamForm({ ...examForm, date: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="enote" className="text-lg">Nota (opcional)</Label>
                <Input id="enote" data-testid="exam-note-input" className="mt-2 h-14 text-lg"
                  value={examForm.note} onChange={(e) => setExamForm({ ...examForm, note: e.target.value })} />
              </div>
            </div>
            <Button data-testid="exam-submit-btn" disabled={!examForm.subject || !examForm.date}
              className="mt-4 h-14 w-full bg-centro text-lg hover:bg-centro-hover sm:w-auto"
              onClick={() => act(async () => {
                if (editing) await http.put(`/exams/${editing}`, { student_id: student.id, ...examForm });
                else await http.post("/exams", { student_id: student.id, ...examForm });
                setEditing(null);
                setExamForm({ subject: "", date: "", note: "" });
              }, editing ? "Examen actualizado." : "Examen añadido.")}>
              {editing ? "Guardar los cambios" : "Añadir examen"}
            </Button>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div>
                <h3 className="font-head text-xl font-medium">Exámenes previstos</h3>
                <ul className="mt-3 space-y-3" data-testid="exams-list">
                  {myExams.length === 0 && <li className="text-lg text-ink-700">No hay exámenes registrados.</li>}
                  {myExams.map((e) => (
                    <li key={e.id} className="rounded-xl border border-border p-4" data-testid={`exam-${e.id}`}>
                      <p className="text-lg font-medium">{e.subject} — {e.date}</p>
                      {e.note && <p className="text-base text-ink-700">{e.note}</p>}
                      <div className="mt-3 flex gap-3">
                        <Button data-testid={`exam-edit-${e.id}`} variant="outline" className="h-12 text-base"
                          onClick={() => { setEditing(e.id); setExamForm({ subject: e.subject, date: e.date, note: e.note || "" }); }}>
                          Editar
                        </Button>
                        <Button data-testid={`exam-delete-${e.id}`} variant="outline" className="h-12 text-base"
                          onClick={() => act(() => http.delete(`/exams/${e.id}`), "Examen borrado.")}>
                          Borrar
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 font-head text-xl font-medium">Calendario del mes</h3>
                <ExamCalendar exams={myExams} />
              </div>
            </div>
          </Card>

          {/* 4. MODIFICAR MATRÍCULA */}
          <Card className="mb-8" data-testid="section-modificar">
            <h2 className="font-head text-2xl font-medium">Modificar la matrícula</h2>
            <p className="mt-2 text-lg text-ink-700">
              Corrija aquí los datos del alumno si se introdujo algo por error. Si cambia la etapa, se
              liberarán las franjas que no correspondan a la nueva agrupación y podrá elegir otras
              desde el horario.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-lg">Nombre del alumno</Label>
                <Input data-testid="edit-student-name-input" className="mt-2 h-14 text-lg"
                  value={editForm.name ?? student.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-lg">Etapa educativa</Label>
                <select data-testid="edit-student-group-select"
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  value={editForm.stage_group ?? student.stage_group}
                  onChange={(e) => setEditForm({ ...editForm, stage_group: e.target.value })}>
                  {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-lg">Curso</Label>
                <Input data-testid="edit-student-grade-input" className="mt-2 h-14 text-lg"
                  value={editForm.grade ?? student.grade ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, grade: e.target.value })} />
              </div>
            </div>
            <Button data-testid="save-student-btn" className="mt-4 h-14 bg-centro text-lg hover:bg-centro-hover"
              onClick={() => act(async () => {
                await http.put(`/students/${student.id}`, {
                  name: editForm.name ?? student.name,
                  stage_group: editForm.stage_group ?? student.stage_group,
                  grade: (editForm.grade ?? student.grade) || null,
                });
                setEditForm({});
              }, "Matrícula actualizada correctamente.")}>
              Guardar los cambios de la matrícula
            </Button>
          </Card>

          {/* 4b. PAUSA TEMPORAL */}
          <PauseCard name={student.name} pauseMonth={student.pause_month} accent="centro"
            testid="pause-student"
            onPause={(month) => act(() => http.post(`/students/${student.id}/pause`, { month }),
              "Pausa registrada. La matrícula se reactivará automáticamente al mes siguiente.")}
            onCancel={() => act(() => http.delete(`/students/${student.id}/pause`),
              "Pausa anulada.")} />

          {/* 5. DAR DE BAJA */}
          <Card className="mb-8 border-red-200" data-testid="section-baja">
            <h2 className="font-head text-2xl font-medium">Dar de baja a {student.name}</h2>
            <p className="mt-2 text-lg text-ink-700">
              Al dar de baja se eliminarán de la web todos los datos de {student.name} (horario,
              ausencias, exámenes y mensualidades) y sus plazas quedarán libres de inmediato para
              otras familias. Esta acción no se puede deshacer. Si solo necesita interrumpir las
              clases un mes, utilice la pausa temporal.
            </p>
            {confirmBaja === student.id ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <Button data-testid="confirm-baja-student-btn" className="h-14 bg-red-700 text-lg hover:bg-red-800"
                  onClick={() => act(async () => {
                    await http.delete(`/students/${student.id}`);
                    setActive(null);
                    setConfirmBaja(null);
                  }, `${student.name} ha sido dado/a de baja y sus datos se han eliminado.`)}>
                  Sí, dar de baja definitivamente
                </Button>
                <Button data-testid="cancel-baja-student-btn" variant="outline" className="h-14 text-lg"
                  onClick={() => setConfirmBaja(null)}>
                  No, mantener la matrícula
                </Button>
              </div>
            ) : (
              <Button data-testid="baja-student-btn" variant="outline" className="mt-4 h-14 text-lg"
                onClick={() => setConfirmBaja(student.id)}>
                Dar de baja a este alumno
              </Button>
            )}
          </Card>
        </>
      )}
    </Shell>
  );
}
